<template>
  <v-row justify="center">
    <v-dialog 
      v-model="visible" 
      width="550px" 
      scrollable
    >
      <v-card>

        <v-card-title>{{ $t("Jobs") }}</v-card-title>
        <v-divider></v-divider>

        <v-card-text class="card-text">
          <v-list dense>
            <v-list-item v-for="item in jobs.slice().reverse()" :key="item.id">
              <v-list-item-avatar>
                <v-progress-circular
                  :value="item.progress"
                  :color="color(item.status)"
                ></v-progress-circular>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title v-text="item.patName"></v-list-item-title>
                <v-list-item-subtitle
                  v-text="item.accessionNo"
                ></v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-list-item-action-text v-text="item.action">
                </v-list-item-action-text>
                <v-list-item-action-text
                  class="action-text"
                  v-if="canCancel(item)"
                  @click="cancelJob(item, $event)"
                >
                  {{ $t("STOP") }}
                </v-list-item-action-text>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn text @click="clearHistory()">{{ $t("clear history") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="$emit('onHideJobsModal')">
            {{ $t("close") }}
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import axios from "axios";
import EventBus from "../../../event-bus";

export default {
  name: "JobsModal",
  props: {
    websocketConfig: Object,
  },
  data: () => ({
    jobs: [],
    websocket: null,
    visible: false,
  }),
  watch: {
    visible: function(val) {
      if (val == false) this.$emit('onHideJobsModal');
    }
  },
  methods: {
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    canCancel(item) {
      if (typeof item.signal !== "undefined") {
        if (item.signal === "Cancel") return false;
      }

      if (
        item.status === "New" ||
        item.status === "Pending" ||
        item.status === "Warning"
      ) {
        return true;
      } else {
        return false;
      }
    },
    color(status) {
      if (status === "Failure") {
        return "red";
      } else if (status === "Warning") {
        return "orange";
      } else if (status === "Cancel") {
        return "yellow darken-1";
      } else if (status === "Success") {
        return "blue darken-1";
      } else {
        return "primary";
      }
    },
    cancelJob(item, event) {
      // Modify stop color
      event.target.classList.replace("action-text", "action-text-pressed");
      // Send signal
      axios({
        url: "/api/v1/dicom/jobs/id/" + item.id,
        method: "POST",
        data: { signal: "Cancel" },
      })
        .then((response) => {
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          }
        })
        .catch((error) => {
          this.sendError(error);
        });
    },
    clearHistory() {
      axios({
        url: "/api/v1/dicom/jobs/owner/" + this.$store.getters.username,
        method: "DELETE",
      })
        .then((response) => {
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          }
        })
        .catch((error) => {
          this.sendError(error);
        });
    },
    calculateProgress(job) {
      const total =
        Number(job.ops.remaining) +
        Number(job.ops.completed) +
        Number(job.ops.failed) +
        Number(job.ops.warning);
      const remaining = Number(job.ops.remaining);

      if (job.status === "Failure" || job.status === "Cancel") {
        return 100;
      }

      if (isNaN(total) || isNaN(remaining)) {
        return 0;
      } else if (total - remaining === 0) {
        return 0;
      } else {
        return (((total - remaining) * 100) / total).toFixed(0);
      }
    },
    updateJobs(json) {
      let srcJobs = JSON.parse(json);
      let jobs = [];
      
      for (let i = 0; i < srcJobs.length; i++) {
        let job = {
          id: srcJobs[i].id,
          dest: srcJobs[i].dest,
          status: srcJobs[i].status,
          progress: this.calculateProgress(srcJobs[i]),
        };

        // STORE SCU Web Job
        if (typeof srcJobs[i].data.StoreSCUWeb !== "undefined") {
          job.patName = srcJobs[i].data.StoreSCUWeb.patName;
          job.accessionNo = srcJobs[i].data.StoreSCUWeb.accessionNo;
          job.action = "SEND";
        }

        // MOVE SCU Web Job
        if (typeof srcJobs[i].data.MoveSCUWeb !== "undefined") {
          job.patName = srcJobs[i].data.MoveSCUWeb.patName;
          job.accessionNo = srcJobs[i].data.MoveSCUWeb.accessionNo;
          job.action = "RETRIEVE";
        }

        jobs.push(job);
      }

      this.jobs = jobs;
      this.visible = true;
    },
  },
  created() {
    if (this.websocketConfig.ssl) {
      this.websocket = new WebSocket('wss://'+location.hostname+':'+this.websocketConfig.port);
    } else {
      this.websocket = new WebSocket('ws://'+location.hostname+':'+this.websocketConfig.port);
    }

    this.websocket.onopen = () => {
      this.websocket.send('{"jobsAll":"'+this.$store.getters.username+'"}');
    }

    this.websocket.onmessage = (event) => {
      this.websocket.send('OK');
      this.updateJobs(event.data);
    }
  },
  destroyed() {
    this.websocket.close();
    this.websocket = null;
  }
};
</script>

<style scoped>
.card-text {
  min-height: 200px;
  max-height: 600px;
}
.action-text {
  color: red !important;
  cursor: pointer;
}
.action-text-pressed {
  color: rgba(0,0,0,0.6) !important;
}
</style>